import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { DropdownMenu, StatusTag, type DropdownMenuItem } from '@/components/ui';
import type { Organization } from '@/api/orgAdmin';
import { OrgAvatar } from './OrgAvatar';
import { getOrgStatusConfig, formatCountVN, orgHealth } from './orgFormat';
import styles from './OrganizationsTable.module.css';

export interface OrgRowStats {
    // EAA-1818: was `tenantCount` (OrganizationAppScope rows, which do not
    // nest) — the design's "Đơn vị" column is the CHILD-ORG count
    // (`Organization.parent`), a different backend entity. Renamed, not
    // added alongside, since nothing in this table reads a tenant count any
    // more (the quick-peek drawer still shows real tenants, but it reads its
    // own `tenants` list directly in `OrganizationsPage.tsx`, not this map).
    unitCount: number | undefined;
    accountCount: number | undefined;
}

interface OrganizationsTableProps {
    organizations: Organization[];
    stats: Record<string, OrgRowStats>;
    selectedOrgId: string | null;
    onPeek: (org: Organization) => void;
    onOpenDetail: (org: Organization) => void;
    /**
     * EAA-1891: open the cross-tree move dialog for this row. OPTIONAL, and the
     * row action is omitted when it is absent — the move is super-admin only
     * (D-09), so the page passes this only for a super-admin. Hiding the entry is
     * UX; `POST /organizations/{id}/move` is the real gate.
     */
    onMoveIntoOrg?: (org: Organization) => void;
}

/**
 * EAA-1815: `/organizations` list table — frame 21 lines 327-650.
 * EAA-1818: "Đơn vị" (child-org count) replaced the old tenant-count-based
 * "Cơ sở" column — `/organizations` lists ROOT orgs only, and its per-row
 * health/count now reflects the đơn vị tree (`Organization.parent`), not
 * `OrganizationAppScope` (app scope, a different, non-nesting entity).
 *
 * "Đơn vị" and "Tài khoản" are not on the `Organization` payload itself (the
 * BE list serializer only returns id/code/name/is_active/branding/timestamps
 * — verified against `services/portal-api/portal_api/directory/org_serializers.py`).
 * Both columns come from a per-row secondary fetch the parent page runs via
 * `useQueries` (child-org count for the count + health, assignments for the
 * account total) — real numbers from real, already-integrated endpoints,
 * never a placeholder. Until a row's fetch resolves the cell shows a
 * skeleton dash, never a guessed number.
 *
 * EAA-1804: LEFT as a hand-rolled `<table>`, not migrated to kit `DataTable`.
 * This list's row click opens `OrganizationPeekDrawer` — a click-anywhere-on-
 * the-row action — and `DataTable` (packages/ui) exposes no `onRow`/row-click
 * prop, only a checkbox-style `rowSelection`. Adopting it would have dropped
 * the peek behaviour, not preserved it, so this table keeps its own markup
 * and the row's real accessibility gap (no keyboard access to "peek") was
 * fixed directly instead — see the `role`/`tabIndex`/`onKeyDown` on the `<tr>`
 * below. The row-click contract (`.fit-scroll` on this component's own root,
 * matched by the page's own `.tableRegion` + `.fit-grow` in `OrganizationsPage.tsx`)
 * is unchanged.
 */
export function OrganizationsTable({
    organizations,
    stats,
    selectedOrgId,
    onPeek,
    onOpenDetail,
    onMoveIntoOrg,
}: OrganizationsTableProps) {
    const { t } = useTranslation();
    const statusConfig = useMemo(() => getOrgStatusConfig(t), [t]);

    return (
        <div className="portal-table-scroll fit-scroll">
            <table className={`portal-admin-table ${styles.table}`}>
                <thead>
                    <tr>
                        <th className={styles.headCell}>{t('admin.organizations.orgWord')}</th>
                        <th className={styles.headCell}>{t('admin.organizations.columnCode')}</th>
                        <th className={styles.headCell}>
                            {t('admin.organizations.columnUnitCount')}
                        </th>
                        <th className={styles.headCell}>
                            {t('admin.organizations.columnAccountCount')}
                        </th>
                        <th className={styles.headCell}>{t('admin.organizations.columnStatus')}</th>
                        <th className={styles.headCell}>
                            <span className="portal-sr-only">{t('common.actions')}</span>
                        </th>
                    </tr>
                </thead>
                <tbody>
                    {organizations.map((org) => {
                        const rowStats = stats[org.id] ?? {
                            unitCount: undefined,
                            accountCount: undefined,
                        };
                        const selected = org.id === selectedOrgId;
                        // EAA-1818: `orgHealth`'s third arg picks which "empty" state to
                        // return — this list's row pill means "no child đơn vị", which is
                        // a distinct state from `OrgDetailHeader`'s "no cơ sở/tenant" (that
                        // page keeps calling `orgHealth` with its default, unchanged).
                        const health = orgHealth(org.is_active, rowStats.unitCount, 'no_unit');
                        const muted = health === 'inactive';
                        const avatarTone = selected ? 'accent' : muted ? 'muted' : 'neutral';

                        const rowActions: DropdownMenuItem[] = [
                            {
                                key: 'peek',
                                label: t('admin.organizations.actionPeek'),
                                onSelect: () => onPeek(org),
                            },
                            {
                                key: 'open',
                                label: t('admin.organizations.openOrgPage'),
                                onSelect: () => onOpenDetail(org),
                            },
                        ];
                        // EAA-1891: the hierarchy action goes last, after the two
                        // read actions — it restructures two trees at once, so it
                        // must not sit where a mis-click on "Mở trang tổ chức"
                        // lands.
                        if (onMoveIntoOrg) {
                            rowActions.push({
                                key: 'move',
                                label: t('admin.organizations.move.action'),
                                onSelect: () => onMoveIntoOrg(org),
                            });
                        }

                        return (
                            // EAA-1804: this row's primary "peek" action was mouse-only — no
                            // `role`, `tabIndex` or `onKeyDown`, a real WCAG 2.1.1 (keyboard)
                            // failure. Left as a hand-rolled `<table>` rather than migrated to
                            // kit `DataTable` (see the file's own top-of-file note): `DataTable`
                            // has no row-click/selection API that can drive an external "peek"
                            // panel, only a checkbox `rowSelection` — converting would drop this
                            // behaviour, not preserve it. Fixed directly instead: `role="button"`
                            // + `tabIndex` + `onKeyDown` (Enter/Space) give the row the same
                            // keyboard affordance `onClick` already gave the mouse. The row menu
                            // `<td>` keeps stopping propagation so Enter/Space on ITS OWN button
                            // does not also fire the row's peek action.
                            <tr
                                key={org.id}
                                className={
                                    styles.row +
                                    (selected ? ' is-selected' : '') +
                                    (muted ? ' is-muted' : '')
                                }
                                onClick={() => onPeek(org)}
                                onKeyDown={(event) => {
                                    if (event.target !== event.currentTarget) return;
                                    if (event.key === 'Enter' || event.key === ' ') {
                                        event.preventDefault();
                                        onPeek(org);
                                    }
                                }}
                                role="button"
                                tabIndex={0}
                                aria-label={t('admin.organizations.actionPeek') + ' — ' + org.name}
                                style={{ cursor: 'pointer' }}
                            >
                                <td className={styles.firstCell}>
                                    <span className={styles.orgCell}>
                                        <OrgAvatar name={org.name} size={30} tone={avatarTone} />
                                        <span className={styles.orgName}>{org.name}</span>
                                    </span>
                                </td>
                                <td>
                                    <code>{org.code}</code>
                                </td>
                                <td>
                                    {rowStats.unitCount === undefined
                                        ? '…'
                                        : formatCountVN(rowStats.unitCount)}
                                </td>
                                <td>
                                    {rowStats.accountCount === undefined
                                        ? '…'
                                        : formatCountVN(rowStats.accountCount)}
                                </td>
                                <td>
                                    <StatusTag status={health} config={statusConfig} />
                                </td>
                                <td onClick={(event) => event.stopPropagation()}>
                                    <DropdownMenu
                                        items={rowActions}
                                        triggerAriaLabel={t(
                                            'admin.organizations.rowActionsAriaLabel',
                                            { name: org.name }
                                        )}
                                    />
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
}

export default OrganizationsTable;
